import { cn } from '@/lib/utils';
import { FOCUS_HIGHLIGHT_CLASS, useFocusHighlight } from '@/hooks/useFocusHighlight';
import type { PostingRecoveryView } from '@shared/posting/postingRecovery';
import { useFlashTarget } from '../hooks/useFlashTarget';
import { FloatingPostingRecoveryPanel } from './FloatingPostingRecoveryPanel';

/**
 * N8 (86d3w2kez) — everything that happens when someone ARRIVES from a notification, in one wrapper.
 *
 * Each of the four detail pages wraps its posting banner in this and gets three behaviours at once:
 * the one-shot `?flash=` marker is consumed and stripped, the banner is scrolled into view and ringed
 * (statically, under `prefers-reduced-motion`), and the floating recovery panel is raised if anything
 * is actually failing.
 *
 * <b>Why a wrapper rather than three hooks per page.</b> Four pages × three behaviours is twelve
 * chances for one page to quietly do it differently — and the pages are 1000+ lines each, which is
 * exactly where a subtly different fourth copy hides. This is one insertion point per page.
 *
 * <b>The panel and the banner are the same data.</b> `views` feeds both, so the floating prompt cannot
 * disagree with the card underneath it — which is the failure mode a second fetch here would create.
 */
export function PostingArrivalSurface({
  views,
  onRetry,
  onReconcile,
  onCancel,
  isBusy = false,
  children,
  className,
}: {
  /** Every posting job the page knows about, already normalised. Settled ones may be included. */
  views: PostingRecoveryView[];
  onRetry?: (view: PostingRecoveryView) => void;
  onReconcile?: (view: PostingRecoveryView) => void;
  onCancel?: (view: PostingRecoveryView) => void;
  isBusy?: boolean;
  /** The page's own inline posting banner. */
  children: React.ReactNode;
  className?: string;
}) {
  const flashJobId = useFlashTarget();
  const arrived = flashJobId !== null;

  const { ref, isHighlighted } = useFocusHighlight<HTMLDivElement>(arrived);

  return (
    <>
      <div
        ref={ref}
        className={cn('rounded-lg', isHighlighted && FOCUS_HIGHLIGHT_CLASS, className)}
      >
        {children}
      </div>

      {/* Raised ONLY on arrival. The banner above is always there; a floating panel that appeared
          every time a post failed would be in the way of the person already looking at the page. */}
      {arrived && (
        <FloatingPostingRecoveryPanel
          views={views}
          onRetry={onRetry}
          onReconcile={onReconcile}
          onCancel={onCancel}
          isBusy={isBusy}
        />
      )}
    </>
  );
}
